<template>
  <div class="text-subtitle2 text-bold q-mx-sm">プレビュー</div>
  <div class="column q-mx-sm q-mt-md q-mb-sm">
    <div class="col flex">
      <img
        :src="value.image_url"
        alt="menu image"
        style="width: 100%; height: 100%; object-fit: cover"
        v-if="value.image_url"
      />
    </div>
    <div class="col row q-pa-md relative-position flex items-center justify-center" style="background: #34425f">
      <q-icon name="keyboard" color="white" size="sm" style="position: absolute; left: 12px" />
      <div class="text-white">{{ value.chat_bar_text }}</div>
    </div>
  </div>
  <q-tabs
    no-caps
    dense
    align="left"
    v-model="tabs"
    :class="isDefaultSelected ? 'text-grey' : 'text-black'"
    :active-color="isDefaultSelected ? 'brand-grey' : 'black'"
    :indicator-color="isDefaultSelected ? 'brand-grey' : 'black'"
    content-class="horizontal-line q-mx-sm q-px-sm"
  >
    <q-tab name="condition" label="表示条件" />
    <q-tab name="user" label="ユーザー" @click="onUserTabClick" />
  </q-tabs>
  <q-tab-panels v-model="tabs">
    <q-tab-panel name="condition">
      <div class="text-subtitle2" :class="isDefaultSelected ? 'text-grey' : 'text-black'">
        {{
          isDefaultSelected
            ? 'デフォルトに設定されているリッチメニューのため、条件設定不要です。'
            : '配信対象のオーディエンス、配信対象外のオーディエンスを選択してください。'
        }}
      </div>
      <q-btn class="full-width q-mt-md" :class="isDefaultSelected ? 'btn-save-grey' : 'btn-save'" @click="setDefault">
        {{ isDefaultSelected ? 'デフォルトに設定済み' : 'デフォルトに設定する' }}
      </q-btn>
      <div
        class="row q-mt-sm q-px-sm q-py-xs justify-between"
        :class="isDefaultSelected ? 'bg-brand-grey' : 'bg-brand'"
      >
        <div class="text-white text-subtitle2">オーディエンス</div>
        <q-icon name="launch" color="white" size="sm" />
      </div>
      <div class="q-mt-sm">
        <AudienceOrNotSetting
          v-model="value.audience"
          :isTestMode="isTestMode"
          :showToolbar="false"
          @update:handleLoading="handleLoading"
          ref="audienceOrNotTest"
          v-if="!isDefaultSelected"
        />
        <template v-else>
          <div class="row items-center text-grey">
            <div class="col-auto">▶&nbsp;{{ $t('label.story.not_selected') }}</div>
            <div class="col q-ml-sm" style="border-top: 1px dashed; border-color: grey"></div>
          </div>
          <div class="row items-center text-green">
            <div class="col-auto">▶&nbsp;{{ $t('label.story.eligible') }}</div>
            <div class="col q-ml-sm" style="border-top: 1px dashed; border-color: green"></div>
          </div>
          <div class="row items-center text-brand-red">
            <div class="col-auto">▶&nbsp;{{ $t('label.story.not_eligible') }}</div>
            <div class="col q-ml-sm" style="border-top: 1px dashed; border-color: #c20b2a"></div>
          </div>
        </template>
      </div>
    </q-tab-panel>
    <q-tab-panel name="user">
      <div class="text-subtitle2 text-black">配信対象となるユーザーの一覧です。</div>
      <div class="row justify-between q-mt-md q-ml-sm">
        <div class="text-black text-bold">選択されたオーディエンスサイズ：</div>
        <div class="text-black text-bold">{{ totalUsers ?? 0 }}</div>
      </div>
      <div class="row justify-between q-mt-sm q-ml-sm">
        <div class="text-brand-red text-bold">除外されたオーディエンスサイズ：</div>
        <div class="text-brand-red text-bold">{{ audienceNotSize ?? 0 }}</div>
      </div>
      <CLoading :loading="loading" />
      <q-table
        bordered
        flat
        dense
        separator="cell"
        :rows="users"
        v-if="users"
        v-model:pagination="pagination"
        :rows-per-page-options="rowPerPageOptions"
        :wrap-cells="true"
        row-key="user_id"
        ref="tableRef"
        :no-data-label="$t('messages.no_data_available')"
        class="q-mt-md text-grey-8"
        @request="onRequest"
      >
        <template v-slot:header="props">
          <q-tr>
            <q-th :props="props" key="display_name"
              ><span class="text-bold">{{ $t('label.audience_user.display_name') }}</span></q-th
            >
            <q-th :props="props" key="created_at" style="width: 150px"
              ><span class="text-bold">{{ $t('label.users.signed_up_at') }}</span></q-th
            >
          </q-tr>
        </template>
        <template v-slot:body="props">
          <q-tr :props="props">
            <q-td key="display_name" :props="props">
              <a :href="onProfileUrl(props.row)" class="cursor-pointer hyper-link-chart" target="_blank">
                <UserAvatar :size="24" :user="getUserInfo(props.row.display_name, props.row.picture_url)" />
                <span class="q-ml-sm hyper-link-user">{{ props.row.display_name }}</span>
              </a></q-td
            >
            <q-td key="created_at" :props="props">{{ props.row.created_at }}</q-td>
          </q-tr>
        </template>
      </q-table>
    </q-tab-panel>
  </q-tab-panels>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { maska } from 'maska'
import { IDateRangePicker, IRichMenu, IRichMenuAudienceSetting, IUser } from '@/utils/types'
import AudienceOrNotSetting from '@/components/story/common/selectors/AudienceOrNotSettingV2.vue'
import AudieneceDndList from '@/components/story/common/selectors/AudieneceDndList.vue'
import UserAvatar from '@/components/user/UserAvatar.vue'
import { DatetimeUtils } from '@/utils/datetime'
import { ACTION_AUDIENCE } from '@/store/actions'
import CLoading from '@/components/common/ui/CLoading.vue'
import { constant } from '@/utils/constants'

@Options({
  components: { CLoading, UserAvatar, AudieneceDndList, AudienceOrNotSetting },
  directives: { maska },
  emits: ['update:modelValue', 'setDefaultRichMenu'],
})
export default class RichMenuConditionSetting extends Vue {
  @Prop()
  modelValue!: IRichMenuAudienceSetting

  @Prop()
  defaultRichMenu!: IRichMenu

  @Prop({ default: false })
  isTestMode = false

  @Prop()
  appId!: string

  loading = false
  tabs = 'condition'
  dateRange: IDateRangePicker = {
    startDate: '',
    endDate: '',
  }

  totalUsers = 0
  users: IUser[] = []
  pagination = {
    sortBy: 'updated_at',
    descending: true,
    page: 1,
    rowsPerPage: constant.ROW_PER_PAGE,
    rowsNumber: 10,
  }

  get value() {
    return this.modelValue
  }

  set value(value: IRichMenuAudienceSetting) {
    this.$emit('update:modelValue', value)
  }

  get isDefaultSelected() {
    return this.value._id === this.defaultRichMenu._id
  }

  get audienceNotSize() {
    if (this.value.audience && this.value.audience.audience_not) {
      return this.value.audience.audience_not.reduce((sum, item) => {
        return sum + (item.audience_size ?? 0)
      }, 0)
    }
    return 0
  }

  get rowPerPageOptions() {
    return constant.ROW_PER_PAGE_OPTIONS
  }

  setDefault() {
    this.$emit('setDefaultRichMenu', this.value._id)
  }

  handleLoading(val) {
    console.log(val)
  }

  onProfileUrl(data) {
    // Construct the URL as a string for direct navigation
    return this.$router.resolve({
      name: 'app_line_user_profile',
      params: {
        app_id: this.appId,
        user_id: data.user_id,
        uid: data._id,
        from_date: DatetimeUtils.formatDate(this.dateRange.startDate),
        to_date: DatetimeUtils.formatDate(this.dateRange.endDate),
        is_test_mode: this.isTestMode.toString(),
      },
    }).href
  }

  getUserInfo(display_name, picture_url) {
    const u: IUser = {
      photo_url: picture_url,
      display_name: display_name,
    }
    return u
  }

  async onUserTabClick() {
    setTimeout(() => {
      this.$refs.tableRef.requestServerInteraction()
    }, 100)
  }

  async onRequest(props) {
    const { page, rowsPerPage, sortBy, descending } = props.pagination
    const filter = props.filter
    this.loading = true
    const returnedData = await this.fetchFromServer(page, rowsPerPage, filter, sortBy, descending)
    if (returnedData) {
      console.log(returnedData)
      // eslint-disable-next-line
      const items: [] = returnedData['items']
      this.users.splice(0, this.users.length, ...items)
      // eslint-disable-next-line
      this.pagination.rowsNumber = returnedData['total_items']
      // eslint-disable-next-line
      this.totalUsers = returnedData['total_items']
    }
    this.pagination.page = page
    this.pagination.rowsPerPage = rowsPerPage
    this.pagination.sortBy = sortBy
    this.pagination.descending = descending

    this.loading = false
  }

  async fetchFromServer(page, rowsPerPage, filter, sortBy, descending) {
    const items = await this.$store.dispatch(ACTION_AUDIENCE.GET_USERS_BY_AUDIENCE_OR_NOT, {
      app_id: this.appId,
      is_test_mode: this.isTestMode,
      audience_or: this.value.audience?.audience_or ?? [],
      audience_not: this.value.audience?.audience_not ?? [],
      filter: {
        page: page,
        per_page: rowsPerPage,
        filter: filter,
        sort_by: sortBy,
        descending: descending,
      },
    })

    return items
  }

  @Watch('value', { immediate: true })
  onValueChange() {
    this.tabs = 'condition'
  }
}
</script>

<style scoped lang="scss">
:deep(.q-tab--active) {
  background: none;
}
:deep(.q-tab-panel) {
  border: none;
}
:deep(.q-tabs__content--align-justify .q-tab) {
  flex: 0 0 auto;
}
</style>
